import React, { FC, useEffect, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { useJourneyApi } from '../journey-api-context';
import { JourneyFilterState, JourneySegment } from '../../../../models/journey-explorer/journey-explorer';
import { errorMessage, formatNumber } from '../journey-explorer-utils';

export const EXPORT_LIMIT = 50_000;
const EXPORT_ERROR = 'The export could not be created.';

interface ExportCustomersProps {
	filters: JourneyFilterState;
	segment?: JourneySegment;
	pathKey?: string;
	customers: number | null;
	compact?: boolean;
}

const fileName = () => `journey-customers-${new Date().toISOString().slice(0, 10)}.csv`;

const download = (blob: Blob) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName();
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

const ExportCustomers: FC<ExportCustomersProps> = ({ filters, segment, pathKey, customers, compact = false }) => {
	const apiService = useJourneyApi();
	const [open, setOpen] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const onDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		window.addEventListener('mousedown', onDown);
		return () => window.removeEventListener('mousedown', onDown);
	}, [open]);

	if (customers === 0) {
		return null;
	}

	const exportCsv = async () => {
		setBusy(true);
		setError(null);
		try {
			download(await apiService.exportCustomers({ filters, segment, path_key: pathKey }));
			setOpen(false);
		} catch (caught) {
			setError(errorMessage(caught, EXPORT_ERROR));
		} finally {
			setBusy(false);
		}
	};

	const count = customers === null ? '' : `${formatNumber(customers)} `;

	return (
		<div
			ref={containerRef}
			className={styles.export}
			onMouseDown={(event) => event.stopPropagation()}
			onClick={(event) => event.stopPropagation()}
		>
			<button
				type="button"
				className={compact ? styles.export_button_compact : styles.panel_action}
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
			>
				Export {count}customers
			</button>
			{open && (
				<div className={styles.export_menu} role="menu">
					<button
						type="button"
						role="menuitem"
						className={styles.export_option}
						disabled={busy}
						onClick={exportCsv}
					>
						{busy ? 'Preparing CSV…' : 'Download CSV'}
					</button>
					<button type="button" role="menuitem" className={styles.export_option} disabled>
						Send to Klaviyo <span className={styles.export_soon}>coming soon</span>
					</button>
					{customers !== null && customers > EXPORT_LIMIT && (
						<span className={styles.export_note}>
							The file holds the {formatNumber(EXPORT_LIMIT)} most recently active customers.
						</span>
					)}
					{error && <span className={styles.export_error}>{error}</span>}
				</div>
			)}
		</div>
	);
};

export default ExportCustomers;
